import { useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight, Crown } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useBasket } from "@/context/BasketContext";
import { formatPrice, multibuyRate } from "@/lib/api";

const FREE_SHIPPING = 40;

export const BasketDrawer = () => {
    const { items, isOpen, setIsOpen, updateQuantity, removeItem, subtotal, savings } = useBasket();
    const navigate = useNavigate();
    const remaining = FREE_SHIPPING - subtotal;

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-md bg-[#141416] border-l border-white/10 text-[#F6F1E7] p-0 flex flex-col"
                data-testid="basket-drawer"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10">
                    <SheetTitle className="font-serif-display text-2xl font-medium text-[#F6F1E7] text-left">
                        Your Basket
                    </SheetTitle>
                    {remaining > 0 ? (
                        <p className="text-xs text-white/50 text-left">
                            {formatPrice(remaining)} away from complimentary delivery
                        </p>
                    ) : (
                        <p className="text-xs text-[#C9B37A] text-left">Complimentary delivery unlocked</p>
                    )}
                </SheetHeader>

                {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6" data-testid="basket-empty-state">
                        <p className="font-serif-display text-xl text-white/60 text-center">
                            Your basket is empty.
                        </p>
                        <button
                            className="btn-luxe btn-gold"
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/shop");
                            }}
                            data-testid="basket-empty-shop-button"
                        >
                            Shop Collection
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                            {items.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-4 pb-5 border-b border-white/[0.07]"
                                    data-testid={`basket-item-${item.id}`}
                                >
                                    <div
                                        className="w-12 h-16 rounded-sm flex-shrink-0 border border-white/10"
                                        style={{
                                            background: `linear-gradient(180deg, #F3EEE3 72%, ${item.band_color || "#0F2A1F"} 72%)`,
                                        }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif-display text-base font-medium truncate">{item.name}</p>
                                        <p className="font-mono-meta text-[10px] text-white/40 mt-0.5">
                                            {formatPrice(item.price)} · {item.weight_g}g
                                        </p>
                                        {multibuyRate(item.category, item.quantity) > 0 ? (
                                            <p className="font-mono-meta text-[9px] text-[#C9B37A] mt-1" data-testid={`basket-multibuy-${item.id}`}>
                                                MULTI-BUY {Math.round(multibuyRate(item.category, item.quantity) * 100)}% APPLIED
                                            </p>
                                        ) : item.category === "cut" && item.quantity === 2 ? (
                                            <button
                                                onClick={() => updateQuantity(item.id, 3)}
                                                className="font-mono-meta text-[9px] text-[#C9B37A]/80 hover:text-[#C9B37A] underline underline-offset-2 mt-1 text-left"
                                                data-testid={`basket-multibuy-nudge-${item.id}`}
                                            >
                                                ADD 1 MORE — SAVE 5%
                                            </button>
                                        ) : null}
                                        <div className="flex items-center gap-3 mt-2">
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                className="w-6 h-6 border border-white/20 rounded-sm flex items-center justify-center hover:border-[#C9B37A] transition-colors"
                                                aria-label="Decrease quantity"
                                                data-testid={`basket-decrease-${item.id}`}
                                            >
                                                <Minus size={11} />
                                            </button>
                                            <span className="text-sm w-5 text-center" data-testid={`basket-quantity-${item.id}`}>
                                                {item.quantity}
                                            </span>
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                className="w-6 h-6 border border-white/20 rounded-sm flex items-center justify-center hover:border-[#C9B37A] transition-colors"
                                                aria-label="Increase quantity"
                                                data-testid={`basket-increase-${item.id}`}
                                            >
                                                <Plus size={11} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <button
                                            onClick={() => removeItem(item.id)}
                                            className="text-white/30 hover:text-[#B42318] transition-colors"
                                            aria-label={`Remove ${item.name}`}
                                            data-testid={`basket-remove-${item.id}`}
                                        >
                                            <X size={15} />
                                        </button>
                                        <span className="text-sm font-semibold text-[#C9B37A]">
                                            {formatPrice(item.price * item.quantity * (1 - multibuyRate(item.category, item.quantity)))}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="px-6 py-5 border-t border-white/10 space-y-4 bg-[#0B0B0C]">
                            {savings > 0 && (
                                <div className="flex justify-between items-baseline">
                                    <span className="eyebrow text-[#C9B37A]/80">Multi-buy savings</span>
                                    <span className="text-sm text-[#C9B37A]" data-testid="basket-savings">
                                        −{formatPrice(savings)}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between items-baseline">
                                <span className="eyebrow text-white/50">Subtotal</span>
                                <span className="font-serif-display text-2xl font-medium" data-testid="basket-subtotal">
                                    {formatPrice(subtotal)}
                                </span>
                            </div>
                            <button
                                className="btn-luxe btn-gold w-full"
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate("/checkout");
                                }}
                                data-testid="basket-checkout-button"
                            >
                                Checkout <ArrowRight size={14} />
                            </button>
                            <button
                                className="w-full flex items-center justify-center gap-2 font-mono-meta text-[9px] tracking-[0.18em] text-[#C9B37A]/70 hover:text-[#C9B37A] transition-colors py-1"
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate("/club");
                                }}
                                data-testid="basket-club-link"
                            >
                                <Crown size={12} /> MEMBERS SAVE UP TO 20% — JOIN THE CLUB
                            </button>
                            <p className="font-mono-meta text-[9px] text-white/30 text-center tracking-wider">
                                FULL TRACEABILITY · SMALL BATCH · BRITISH FARMS
                            </p>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};
